import { extractVerifierMoney } from "./figures";
import { DAY_MS } from "./window";

/**
 * Freshness TTLs on the facts a customer can act on.
 *
 * Inbox Composer, verbatim: "incremental, with freshness TTLs; stale price or
 * availability = insert blocked, not guessed." Planning elements says the same
 * thing about the runtime: "freshness TTLs everywhere, stale price/availability
 * = action BLOCKED, never guessed."
 *
 * A price is not a fact about the world, it is a fact about a moment. Gold
 * moves, a bench rate changes, a supplier quote expires. The KB records when
 * each fact was observed, and until this module existed nothing read that
 * field: a quote captured in a 2017 thread grounded a reply sent today, and
 * the verifier called it exact-matched, because it was. Exact-matching a stale
 * number is precisely the confident wrong answer §0 exists to prevent.
 *
 * The classification is deliberately narrow. Only money perishes here.
 * Turnaround, policy and materials do not rot the way a quote does, and a gate
 * that expired everything would expire itself: the shop would answer "let me
 * check on that" to "do you do repairs", the operator would learn to ignore
 * it, and the one case that mattered would go past unread.
 */

/**
 * A price observed this long ago is no longer today's price.
 *
 * AWAITING RATIFICATION (TA-85). The vault says "freshness TTLs" and never
 * says how long, so this is the conservative reading and not a ruling. It is
 * also a T3 number by nature: it decides which figures the shop is willing to
 * quote, which is the same kind of decision as a discount cap.
 */
export const PRICE_TTL_DAYS = 180;

/**
 * What kind of perishability a fact has, or null for none.
 *
 * "availability" is in the spec sentence and is NOT implemented, because there
 * is no catalog and no stock feed to be stale against. Detecting it from the
 * wording of a fact would be a keyword guess dressed as a gate (TA-85).
 */
export type Perishable = "price";

export interface Freshness {
  readonly perishable: Perishable | null;
  /** Whole days between the observation and now. Negative means a future date. */
  readonly ageDays: number;
  /** Null when nothing about this fact perishes. */
  readonly ttlDays: number | null;
  readonly stale: boolean;
}

/**
 * `observedAt` is typed optional even though `KbChunk` requires it. The type
 * system is not the last line here: chunks are also built by hand in fixtures
 * and by importers reading other people's files, and one that arrives without
 * a date must be refused, not thrown on. A gate that crashes stops the whole
 * reply instead of declining one number, which is an outage wearing a gate's
 * name.
 */
export function factFreshness(
  fact: string,
  observedAt: string | undefined,
  now: Date,
  ttlDays: number = PRICE_TTL_DAYS,
): Freshness {
  // TA-566 (figbind-8): a price the verifier reads ("65 dollars", "$5k") ages
  // like one it writes with a "$", or a fact stating it could never go stale.
  const perishable: Perishable | null = extractVerifierMoney(fact).length > 0 ? "price" : null;
  if (perishable === null) {
    return { perishable: null, ageDays: 0, ttlDays: null, stale: false };
  }

  const observed = new Date(observedAt ?? "");
  if ((observedAt ?? "").trim().length === 0 || Number.isNaN(observed.getTime())) {
    // A price of unknown age is not a price anyone can quote. Reading a
    // missing date as "today" is the optimistic reading, and it makes an
    // unreadable fact look current, which is the worst of the three outcomes.
    return { perishable, ageDays: Number.POSITIVE_INFINITY, ttlDays, stale: true };
  }

  const ageDays = Math.floor((now.getTime() - observed.getTime()) / DAY_MS);
  return { perishable, ageDays, ttlDays, stale: ageDays > ttlDays };
}

/** The least a chunk has to carry to be aged. Structural, so core does not
 * need to import the pipeline's `KbChunk` to count its own artifact. */
export interface AgeableFact {
  readonly id: string;
  readonly fact: string;
  readonly observedAt?: string;
}

export interface KbFreshness {
  readonly total: number;
  /** Chunks carrying a figure. The rest cannot be stale by this definition. */
  readonly priced: number;
  /** Oldest first. Empty is a result, not an absence. */
  readonly stale: readonly { readonly id: string; readonly ageDays: number }[];
  readonly ttlDays: number;
}

/**
 * How old the shipped KB is, as a number somebody reads.
 *
 * `factFreshness` blocks one figure at a time, at the moment a customer asked.
 * That is the right place for the gate and the wrong place to learn that a
 * fifth of the corpus expired: one blocked reply looks like a model problem,
 * and only the count says it is a pipeline problem. So the eval report prints
 * this whether or not anything failed (CLAUDE.md section 0: loud to the
 * operator).
 */
export function kbFreshness(
  chunks: readonly AgeableFact[],
  now: Date,
  ttlDays: number = PRICE_TTL_DAYS,
): KbFreshness {
  let priced = 0;
  const stale: { id: string; ageDays: number }[] = [];
  for (const c of chunks) {
    const f = factFreshness(c.fact, c.observedAt, now, ttlDays);
    if (f.perishable === null) continue;
    priced += 1;
    if (f.stale) stale.push({ id: c.id, ageDays: f.ageDays });
  }
  stale.sort((a, b) => b.ageDays - a.ageDays);
  return { total: chunks.length, priced, stale, ttlDays };
}

/**
 * How many days before its TTL a price is said to be expiring (TA-1361).
 *
 * AWAITING RATIFICATION (TA-1361). An implementer reading, not a ruling: a
 * month is long enough for the owner to re-confirm a price on the Shop answers
 * page before the gate starts answering "let me check on that" in its place,
 * and short enough that the warning does not sit on half the book. It decides
 * nothing a customer sees; it only moves when the owner is told.
 */
export const PRICE_WARN_DAYS = 30;

/** One priced fact against its date. */
export interface PriceExpiry {
  /** Whole days since the observation. Infinity for an undated price, as `factFreshness` reads it. */
  readonly ageDays: number;
  /** The TTL minus the age: 0 is the last quotable day, negative is past it. */
  readonly daysLeft: number;
  /** Exactly `factFreshness(...).stale`, so the count never disagrees with the gate. */
  readonly stale: boolean;
  /** Not stale, and `daysLeft` is at most `warnDays`. */
  readonly expiring: boolean;
}

/**
 * The per-chunk half of `kbExpiry`, for the one surface that shows a single
 * fact's date (the Shop answers page). Null for a fact with no figure: only
 * money perishes. Built on `factFreshness`, never beside it, so a price the
 * verifier refuses under stale_price is the price this calls stale, on the
 * same day (TA-1361).
 */
export function priceExpiry(
  fact: string,
  observedAt: string | undefined,
  now: Date,
  ttlDays: number = PRICE_TTL_DAYS,
  warnDays: number = PRICE_WARN_DAYS,
): PriceExpiry | null {
  const f = factFreshness(fact, observedAt, now, ttlDays);
  if (f.perishable === null) return null;
  const daysLeft = ttlDays - f.ageDays;
  return { ageDays: f.ageDays, daysLeft, stale: f.stale, expiring: !f.stale && daysLeft <= warnDays };
}

export interface KbExpiry {
  /** Chunks carrying a figure, an undated one included. */
  readonly priced: number;
  /** Past their date, oldest first. Ids and ages only. */
  readonly stale: readonly { readonly id: string; readonly ageDays: number }[];
  /** Within `warnDays` of their date, soonest first. Ids and days only. */
  readonly expiring: readonly { readonly id: string; readonly daysLeft: number }[];
}

/**
 * The warning before the cliff (TA-1361).
 *
 * `kbFreshness` counts what has already expired, and only the eval report
 * printed it, so nothing the worker, the API or the owner's pages run said a
 * date was why a batch of price questions turned into "let me check" turns
 * and owner escalations. This adds the prices about to expire, so the owner
 * can re-confirm them while they can still be quoted, and the worker's daily
 * sweep raises the stale count as an alert (CLAUDE.md section 0: loud to the
 * operator). Ids and numbers only, never a fact's words: the count travels to
 * a log line, an alert row and a mail.
 *
 * An undated price is counted priced and stale, as in `kbFreshness`: the reply
 * path refuses it, so the count must name it.
 */
export function kbExpiry(
  chunks: readonly AgeableFact[],
  now: Date,
  ttlDays: number = PRICE_TTL_DAYS,
  warnDays: number = PRICE_WARN_DAYS,
): KbExpiry {
  let priced = 0;
  const stale: { id: string; ageDays: number }[] = [];
  const expiring: { id: string; daysLeft: number }[] = [];
  for (const c of chunks) {
    const e = priceExpiry(c.fact, c.observedAt, now, ttlDays, warnDays);
    if (e === null) continue;
    priced += 1;
    if (e.stale) stale.push({ id: c.id, ageDays: e.ageDays });
    else if (e.expiring) expiring.push({ id: c.id, daysLeft: e.daysLeft });
  }
  // Infinity minus Infinity is NaN, which sort reads as equal: two undated
  // prices keep their KB order, both ahead of every dated one.
  stale.sort((a, b) => b.ageDays - a.ageDays);
  expiring.sort((a, b) => a.daysLeft - b.daysLeft);
  return { priced, stale, expiring };
}
